import {DomainObject} from 'shared/store';
import {observable, computed, action, makeObservable} from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import {v4 as uuidv4} from 'uuid';
import {t} from 'shared/core';
import {atTimeOfDay, calendarDay} from 'shared/tools';
import {Employee} from 'main/stores/employees';
import TimeTrackingDimensionValue from './TimeTrackingDimensionValue';
import ScheduleRule from './ScheduleRule';

class Shift extends DomainObject {
  uuid;
  startTime = null;
  endTime = null;
  date = null;
  startTimeString = '';
  endTimeString = '';
  unpaidBreakMinutes = 0;
  note = '';
  publishedAt = null;
  employee = null;
  dimensionValues = [];
  scheduleRule = null;

  _relations = {
    employee: Employee,
    dimensionValues: [TimeTrackingDimensionValue],
    scheduleRule: ScheduleRule
  };

  constructor(other) {
    super();

    makeObservable(this, {
      startTime: observable,
      endTime: observable,
      date: observable,
      startTimeString: observable,
      endTimeString: observable,
      unpaidBreakMinutes: observable,
      note: observable,
      publishedAt: observable,
      employee: observable,
      dimensionValues: observable,
      scheduleRule: observable,
      startTimeFromString: computed,
      endTimeFromString: computed,
      spansMidnight: computed,
      employeeId: computed,
      dimensionValueIds: computed,
      published: computed,
      paidHours: computed,
      timeRange: computed,
      setDimensionValue: action,
      setRepeatEnabled: action
    });

    this.merge(other);
    this.uuid = uuidv4();

    if (this.startTime && !this.startTimeString) {
      const timeFormat = t('components.dates.TIMESTAMP_FORMAT');

      this.date = calendarDay(this.startTime);
      this.startTimeString = moment.utc(this.startTime).format(timeFormat);
      this.endTimeString = moment.utc(this.endTime).format(timeFormat);
    }
  }

  get startTimeFromString() {
    const startTime = atTimeOfDay(this.date, this.startTimeString);

    return startTime && moment(startTime);
  }

  get endTimeFromString() {
    const endTime = atTimeOfDay(this.date, this.endTimeString);
    if (!endTime) return null;

    return this.spansMidnight ? moment(endTime).add(1, 'day') : moment(endTime);
  }

  get spansMidnight() {
    const startTime = atTimeOfDay(this.date, this.startTimeString);
    const endTime = atTimeOfDay(this.date, this.endTimeString);

    return !!startTime && !!endTime && endTime < startTime;
  }

  get employeeId() {
    return _.get(this.employee, 'id', null);
  }

  get dimensionValueIds() {
    return _.map(this.dimensionValues, 'id');
  }

  get published() {
    return !!this.publishedAt;
  }

  get paidHours() {
    if (!this.startTimeFromString || !this.endTimeFromString) return 0;

    const minutes = this.endTimeFromString.clone().utc(true).diff(this.startTimeFromString.clone().utc(true), 'minutes') - Number(this.unpaidBreakMinutes || 0);

    return Math.max(minutes, 0) / 60;
  }

  get timeRange() {
    if (!this.startTimeString || !this.endTimeString) return null;

    return t('components.dates.DATE_SPAN', {
      startDate: this.startTimeString,
      endDate: this.endTimeString
    });
  }

  toJS() {
    return {
      ...super.toJS(),
      startTime: this.startTimeFromString && this.startTimeFromString.clone().utc(true).format(),
      endTime: this.endTimeFromString && this.endTimeFromString.clone().utc(true).format(),
      note: this.note || null,
      scheduleRule: this.scheduleRule && this.scheduleRule.toJS()
    };
  }

  dimensionValueFor(dimension) {
    return _.find(this.dimensionValues, {dimensionId: dimension.id});
  }

  overlaps(startsAt, endsAt) {
    return this.startTimeFromString.isBefore(endsAt) && this.endTimeFromString.isAfter(startsAt);
  }

  differsFrom(other) {
    return !_.isEqual(comparableAttributes(this), comparableAttributes(other));
  }

  scheduleRuleDiffersFrom(other) {
    return !_.isEqual(this.scheduleRule?.toJS(), other.scheduleRule?.toJS());
  }

  copyWith(changes) {
    return Object.assign(new Shift(this), changes);
  }

  setRepeatEnabled(enabled, scheduleRule) {
    this.scheduleRule = enabled ?
      new ScheduleRule(scheduleRule || {repeatDays: [moment(this.date).day()]}) :
      null;
  }

  setDimensionValue(dimension, value) {
    const otherDimensionValues = _.reject(this.dimensionValues, {dimensionId: dimension.id});

    this.dimensionValues = value ? [...otherDimensionValues, new TimeTrackingDimensionValue(value)] : otherDimensionValues;
  }
}

function comparableAttributes(shift) {
  const {startTime, endTime, unpaidBreakMinutes, note} = shift.toJS();

  return {
    startTime,
    endTime,
    unpaidBreakMinutes: Number(unpaidBreakMinutes),
    note,
    employeeId: shift.employeeId,
    dimensionValueIds: _.sortBy(shift.dimensionValueIds)
  };
}

export default Shift;
